import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Divider, PageWrapper, Anchors } from '@/shared'
import { doctorState } from '@App/store'
import {
    type IFeedbackMode,
    type IFeedbackDto,
    type IUpdateFeedback,
    type IAdditionalInfo2
} from '@App/types/doctorsType'

import { DescriptionEditor } from './DescriptionEditor/DescriptionEditor'
import style from './EditDoctor.module.scss'
import { Feedbacks } from './Feedbacks/Feedbacks'
import { GoBack } from './GoBack/GoBack'
import { Name } from './Name/Name'
import { PhotoEditor } from './PhotoEditor/PhotoEditor'

export const EditDoctor = observer(() => {
    const { id } = useParams()

    // const editDoctorPage = useAsyncState(() => doctorState)

    const [isOpenImageEditor, setIsOpenImageEditor] = useState<boolean>(false)
    const [feedbackMode, setFeebackMode] = useState<IFeedbackMode[]>([
        { title: 'Опубликованные', isPublished: true, isChecked: true },
        { title: 'Неопубликованные', isPublished: false, isChecked: false }
    ])

    const anchors = [
        { title: 'Фото', id: '#photo' },
        { title: 'Описание в карточке', id: '#description' },
        { title: 'Отзывы', id: '#feedbacks' }
    ]

    const currentMode = feedbackMode.find((mode) => mode.isChecked)

    const onGetDoctorCard = useCallback(() => {
        if (id && !doctorState.doctorCard) {
            doctorState.onGetDoctorCard(id)
            doctorState.onGetAdditionalInfoByEmployeeId(id)
        }
        return () => {
            doctorState.onSetDoctorCard(null)
            doctorState.onSetAdditionalInfo(null)
        }
    }, [id])

    // const addAdditionalInfoSection = useCallback(
    //     (dto: TAdditionalInfoNewDto, onSuccess: () => void) => {
    //         if (id) {
    //             doctorState.onAddAdditionalInfoSection(id, dto, onSuccess)
    //         }
    //     },
    //     [id]
    // )

    // const removeAdditionalInfoSection = useCallback(
    //     (additionalInfoId: string) => {
    //         if (id) {
    //             doctorState.onRemoveAdditionalInfoSection(id, additionalInfoId)
    //         }
    //     },
    //     [id]
    // )

    // const updateAdditionalInfoSection = useCallback(
    //     (dto: IAdditionalInfoNew) => {
    //         if (id) {
    //             doctorState.onUpdateAdditionalInfoSection(id, dto)
    //         }
    //     },
    //     [id]
    // )

    // const addAdditionalInfoSectionItem = useCallback(
    //     (additionalInfoId: string, dto: IAdditionalInfoItemDto, onSuccess: () => void) => {
    //         if (id) {
    //             doctorState.onAddAdditionalInfoSectionItem(id, additionalInfoId, dto, onSuccess)
    //         }
    //     },
    //     [id]
    // )

    // const removeAdditionalInfoSectionItem = useCallback(
    //     (additionalInfoId: string, itemId: string) => {
    //         if (id) {
    //             doctorState.onRemoveAdditionalInfoSectionItem(id, additionalInfoId, itemId)
    //         }
    //     },
    //     [id]
    // )

    // const updateAdditionalInfoSectionItem = useCallback(
    //     (additionalInfoId: string, dto: IAdditionalInfoItem) => {
    //         if (id) {
    //             doctorState.onUpdateAdditionalInfoSectionItem(id, additionalInfoId, dto)
    //         }
    //     },
    //     [id]
    // )

    const onGetAdditionalInfoActualData = (onSuccess?: () => void) => {
        if (id) {
            doctorState.onGetAdditionalInfoByEmployeeId(id, onSuccess)
        }
    }

    const onUpdateAdditionalInfoHandler = (dto: IAdditionalInfo2[], onSuccess?: () => void) => {
        if (id) {
            doctorState.onUpdateAdditionalInfo({ employeeId: id, additionalInfo: dto }, onSuccess)
        }
    }

    const getFeedbacksById = useCallback(() => {
        if (id && !doctorState.feedbacks && currentMode) {
            doctorState.onGetFeedbacksById(id, currentMode.isPublished)
        }
        return () => {
            doctorState.onSetFeedbacks(null)
        }
    }, [id, currentMode])

    const updateFeedback = useCallback(
        (dto: IUpdateFeedback, onSuccess?: () => void) => {
            if (id && currentMode) {
                // setFeebackMode((prev) => prev.map((mode) => ({ ...mode, isChecked: !mode.isChecked })))
                doctorState.onUpdateFeedback(id, dto, onSuccess)
            }
        },
        [id, currentMode]
    )

    const removeFeedback = useCallback(
        (feedbackId: string) => {
            if (id && currentMode) {
                doctorState.onRemoveFeedback(id, feedbackId, currentMode.isPublished)
            }
        },
        [id, currentMode]
    )

    const uploadImage = useCallback(
        (file: File) => {
            if (id && doctorState.doctorCard) {
                const onSuccess = () => {
                    doctorState.onSetDoctorCard(null)
                    onGetDoctorCard()
                    setIsOpenImageEditor(false)
                }
                doctorState.onUploadImage(id, file, onSuccess, doctorState.doctorCard)
            }
        },
        [id, onGetDoctorCard]
    )

    const removeImage = useCallback(() => {
        if (id && doctorState.doctorCard && doctorState.doctorCard.avatar) {
            const onSuccess = () => {
                doctorState.onSetDoctorCard(null)
                onGetDoctorCard()
                setIsOpenImageEditor(false)
            }
            doctorState.onRemoveImage(id, doctorState.doctorCard.avatar, doctorState.doctorCard, onSuccess)
        }
    }, [id, onGetDoctorCard])

    const addFeedback = useCallback(
        (dto: Omit<IFeedbackDto, 'doctorId'>, onSuccess: () => void) => {
            if (id) {
                doctorState.onAddFeedback({ ...dto, doctorId: id }, onSuccess)
            }
        },
        [id]
    )

    useEffect(onGetDoctorCard, [onGetDoctorCard])
    // useEffect(getDescriptionById, [getDescriptionById])
    useEffect(getFeedbacksById, [getFeedbacksById])

    return (
        <div className={style.container}>
            <PageWrapper>
                <Anchors anchors={anchors} />
                <GoBack />
                <Name doctorCard={doctorState.doctorCard || null} />
                <Divider />
                <div id='#photo' />
                <PhotoEditor
                    photo={doctorState.doctorCard?.avatar || null}
                    isOpen={isOpenImageEditor}
                    onSetIsOpen={setIsOpenImageEditor}
                    isFetching={!doctorState.doctorCard}
                    onUploadImageCallback={uploadImage}
                    onRemoveImageCallback={removeImage}
                />
                <Divider />
                <div id='#description' />
                <DescriptionEditor
                    additionalInfo={doctorState.additionalInfo}
                    onUpdateAdditionalInfo={onUpdateAdditionalInfoHandler}
                    onGetAdditionalInfoActualData={onGetAdditionalInfoActualData}
                    isFetchingAdditionalInfo={doctorState.isFetchingAdditionalInfo}
                />
                <Divider />
                <div id='#feedbacks' />
                <Feedbacks
                    mode={feedbackMode}
                    isPublished={currentMode?.isPublished || false}
                    setMode={setFeebackMode}
                    feedbacksList={doctorState.feedbacks}
                    onUpdateFeedback={updateFeedback}
                    onRemoveFeedback={removeFeedback}
                    onAddFeedback={addFeedback}
                    isFetchingFeedbacks={doctorState.isFetchingFeedbacks}
                />
            </PageWrapper>
        </div>
    )
})
